import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import {
  saveClasses,
  saveAnnotations,
  getAnnotations,
} from "../../utils/ImageDB";
import {
  saveOcrData,
  getProjectClasses,
  saveProjectClasses,
  fetchImageDetails
} from "../../utils/crudFunctions";

import ClassModal from "./ClassModal";
import LeftBar from "./LeftBar";
import RightBar from "./RightBar";
import RectClassModal from "./RectClassModal";
import KonvaStage from "./KonvaStage";

function OCRComponent({ setProjectName }) {
  const { userName, projectName, index } = useParams();
  const [currentIndex, setCurrentIndex] = useState(parseInt(index) - 1 || 0);
  const [image, setImage] = useState(null);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);
  const [imageData, setImageData] = useState({});
  const [classes, setClasses] = useState([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [modalMode, setModalMode] = useState("add");
  const [tool, setTool] = useState("move");
  const [polygons, setPolygons] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showRectModal, setShowRectModal] = useState(false);
  const [rectModalPosition, setRectModalPosition] = useState({ x: 0, y: 0 });
  const [selectedPolygonForModal, setSelectedPolygonForModal] = useState(null);
  const [selectedClassForPolygon, setSelectedClassForPolygon] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [allAnnotations, setAllAnnotations] = useState([]);
  const [showSegments, setShowSegments] = useState(true);
  const [datasetLength, setDatasetLength] = useState(() => {
    const savedLength = localStorage.getItem(`${projectName}_datasetLength`);
    return savedLength ? parseInt(savedLength) : 0;
  });

  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const currentImageRef = useRef(null);
  const abortControllerRef = useRef(new AbortController());
  const navigate = useNavigate();

  const [dimensions, setDimensions] = useState({
    original: { width: 0, height: 0 },
    rendered: { width: 0, height: 0 },
    scale: 1,
  });

  // ------------------ Image Loading ------------------

  const cleanupResources = () => {
    if (currentImageRef.current) {
      if (currentImageRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(currentImageRef.current.src);
      }
      currentImageRef.current.onload = null;
      currentImageRef.current.onerror = null;
      currentImageRef.current = null;
    }
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  };

  const loadCurrentImage = async () => {
    if (isNaN(currentIndex)) return;
    cleanupResources();
    setLoadingCurrent(true);

    try {
      const imageDetails = await fetchImageDetails(projectName, currentIndex);
      if (!imageDetails?.image) {
        throw new Error("No image data received");
      }

      const img = new window.Image();
      currentImageRef.current = img;
      
      const imageLoadPromise = new Promise((resolve, reject) => {
        img.onload = () => {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            resolve(img);
          } else {
            reject(new Error("Invalid image dimensions"));
          }
        };
        img.onerror = () => {
          reject(new Error("Failed to load image"));
        };
      });

      img.src = `data:image/jpeg;base64,${imageDetails.image}`;
      await imageLoadPromise;

      setImage(img);
      setImageData({
        id: imageDetails.id,
        name: imageDetails.filename,
        data: `data:image/jpeg;base64,${imageDetails.image}`
      });

      const maxWidth = window.innerWidth - 260;
      const maxHeight = window.innerHeight - 110;
      const ratio = Math.min(
        maxWidth / img.naturalWidth,
        maxHeight / img.naturalHeight,
        1
      );

      setDimensions({
        original: {
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
        rendered: {
          width: Math.round(img.naturalWidth * ratio),
          height: Math.round(img.naturalHeight * ratio),
        },
        scale: ratio,
      });
    } catch (error) {
      console.error("Error loading image:", error);
      setImageLoaded(false);
    } finally {
      setLoadingCurrent(false);
    }
  };

  useEffect(() => {
    loadCurrentImage();
  }, [currentIndex, projectName]);

  useEffect(() => {
    return () => {
      cleanupResources();
    };
  }, []);

  // ------------------ Classes Management ------------------

  const loadClasses = async () => {
    try {
      const backendClasses = await getProjectClasses(projectName);
      const sorted = backendClasses.sort((a, b) => a.index - b.index);
      setClasses(sorted);
      await saveClasses(sorted);
    } catch (error) {
      console.error("Failed to load classes:", error);
    }
  };

  useEffect(() => {
    loadClasses();
    setProjectName(projectName);
  }, []);

  useEffect(() => {
    getAnnotations().then((saved) => {
      if (saved.length > 0) {
        setAllAnnotations(saved);
      }
    });
  }, []);

  // ------------------ Polygon Management ------------------

  // Load existing annotations for current image
  useEffect(() => {
    if (!image || !dimensions.scale || allAnnotations.length === 0) return;

    const existingAnnotations = allAnnotations.find(
      (ann) => ann.imageIndex === currentIndex
    );

    if (existingAnnotations && existingAnnotations.annotations) {
      const scaledPolygons = existingAnnotations.annotations.map((poly) => {
        // Convert flat array to {x, y} points and apply scaling
        const points = [];
        for (let i = 0; i < poly.points.length; i += 2) {
          points.push({
            x: poly.points[i] / dimensions.scale,
            y: poly.points[i + 1] / dimensions.scale
          });
        }
        
        return {
          ...poly,
          points,
          id: poly.id || `poly_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
      });
      
      setPolygons(scaledPolygons);
    } else {
      setPolygons([]);
    }
  }, [currentIndex, image, allAnnotations, dimensions.scale]);

  // ------------------ Save Functions ------------------

  const handleSaveNext = async () => {
    if (!image) return;

    // Convert polygons to pixel coordinates with scaling applied
    const pixelPolys = polygons.map((poly) => ({
      ...poly,
      points: poly.points.flatMap(pt => [
        Math.round(pt.x * dimensions.scale),
        Math.round(pt.y * dimensions.scale)
      ]),
      class: poly.class,
    }));

    const currentImageData = {
      imageName: imageData.name,
      imageIndex: imageData.id,
      annotations: pixelPolys,
    };

    try {
      const success = await saveOcrData(projectName, currentImageData);
      
      if (success) {
        // Update local state
        setAllAnnotations((prev) => {
          const updated = prev.filter(
            (item) => item.imageIndex !== currentImageData.imageIndex
          );
          return [...updated, currentImageData];
        });

        // Move to next image if not on last one
        if (currentIndex < datasetLength - 1) {
          goToIndex(currentIndex + 1);
        }
      } else {
        alert("Failed to save annotation data. Please try again.");
      }
    } catch (error) {
      console.error("Error saving annotations:", error);
      alert("An error occurred while saving. Please try again.");
    }
  };

  // ------------------ Navigation ------------------

  const goToIndex = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= datasetLength) return;
    cleanupResources();
    setCurrentIndex(newIndex);
    navigate(`/project/${userName}/${projectName}/ocr/${newIndex + 1}`, {
      replace: true,
    });
  }, [datasetLength, userName, projectName, navigate]);

  const handleNext = () => {
    if (currentIndex >= datasetLength - 1) return;
    goToIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex <= 0) return;
    goToIndex(currentIndex - 1);
  };

  // ------------------ Zoom Functions ------------------

  const handleZoom = (direction) => {
    const stage = stageRef.current;
    if (!stage) return;

    const scaleBy = 1.1;
    const oldScale = stage.scaleX();
    const center = {
      x: stage.width() / 2,
      y: stage.height() / 2,
    };

    const newScale = direction === "in" ? oldScale * scaleBy : oldScale / scaleBy;
    setZoomLevel(newScale);

    stage.scale({ x: newScale, y: newScale });

    const mousePointTo = {
      x: (center.x - stage.x()) / oldScale,
      y: (center.y - stage.y()) / oldScale,
    };

    const newPos = {
      x: center.x - mousePointTo.x * newScale,
      y: center.y - mousePointTo.y * newScale,
    };

    stage.position(newPos);
    stage.batchDraw();
  };

  const handleResetZoom = () => {
    const stage = stageRef.current;
    if (!stage) return;
    
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();
    setZoomLevel(1);
  };

  // ------------------ Render ------------------

  if (!imageData?.data) {
    return (
      <div className="max-w-md mx-auto p-6 text-center text-gray-500">
        {loadingCurrent ? 'Loading image...' : 'No image to display'}
      </div>
    );
  }

  return (
    <div className="flex w-screen fixed h-[calc(100vh-3.5rem)] mt-14">
      {/* Sidebar */}
      <LeftBar
        classes={classes}
        setClasses={setClasses}
        setShowClassModal={setShowClassModal}
        setModalMode={setModalMode}
        setNewClassName={setNewClassName}
        projectName={projectName}
        userName={userName}
        polygons={polygons}
        setPolygons={setPolygons}
        selectedPolygonForModal={selectedPolygonForModal}
        setSelectedPolygonForModal={setSelectedPolygonForModal}
        setShowRectModal={setShowRectModal}
        setRectModalPosition={setRectModalPosition}
        stageRef={stageRef}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden h-full">
        {loadingCurrent && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading Image {currentIndex + 1}...
            </div>
          </div>
        )}

        <div className="flex-1 flex justify-center items-center min-h-0 w-full h-full">
          <div className="relative mx-auto h-full w-full" style={{
            width: "100%",
            height: "100%",
            padding: 0,
            margin: 0,
          }}>
            <KonvaStage
              dimensions={dimensions}
              tool={tool}
              polygons={polygons}
              setIsDrawing={setIsDrawing}
              imgRef={imgRef}
              image={image}
              stageRef={stageRef}
              setPolygons={setPolygons}
              isDrawing={isDrawing}
              setRectModalPosition={setRectModalPosition}
              setShowRectModal={setShowRectModal}
              setSelectedPolygonForModal={setSelectedPolygonForModal}
              selectedPolygonForModal={selectedPolygonForModal}
              isLocked={isLocked}
              setShowSegments={setShowSegments}
              showSegments={showSegments}
              scale={dimensions.scale}
            />
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="flex-shrink-0 border-t ">
          <div className="flex items-center justify-between p-1 border-t border-gray-200">
            <button
              onClick={handlePrev}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <FiChevronLeft className="mr-1" />
              Previous
            </button>

            <div className="flex flex-col items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                Image {currentIndex + 1} of {datasetLength}
              </span>
              <h3 className="text-sm font-medium text-gray-600">
                {imageData?.name || `Image ${currentIndex + 1}`}
              </h3>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleNext}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Next
                <FiChevronRight className="ml-1" />
              </button>
              <button
                onClick={handleSaveNext}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                Save & Next
                <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Tools */}
      <RightBar
        setTool={setTool}
        tool={tool}
        zoomLevel={zoomLevel}
        handleZoom={handleZoom}
        handleResetZoom={handleResetZoom}
        setPolygons={setPolygons}
        selectedPolygonForModal={selectedPolygonForModal}
        setSelectedPolygonForModal={setSelectedPolygonForModal}
        setShowRectModal={setShowRectModal}
        setIsLocked={setIsLocked}
        isLocked={isLocked}
        setShowSegments={setShowSegments}
        showSegments={showSegments}
      />

      {/* Modals */}
      {/* {showClassModal && (
        <ClassModal
          modalMode={modalMode}
          newClassName={newClassName}
          setNewClassName={setNewClassName}
          setShowClassModal={setShowClassModal}
          handleSubmit={handleSubmit}
        />
      )} */}
      {showRectModal && (
        <RectClassModal
          position={rectModalPosition}
          classes={classes}
          selectedClass={selectedClassForPolygon}
          setSelectedClass={setSelectedClassForPolygon}
          onClose={() => setShowRectModal(false)}
          onSave={(selectedClass) => {
            setPolygons((prev) =>
              prev.map((poly) =>
                poly.id === selectedPolygonForModal?.id
                  ? { ...poly, class: selectedClass.text }
                  : poly
              )
            );
            setShowRectModal(false);
          }}
        />
      )}
    </div>
  );
}

export default OCRComponent;